<template>
  <div>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
      <div class="tw-col-span-3">
        <ConversationList />
      </div>
      <div class="tw-col-span-9">
        <!-- Empty State when no conversation is selected -->
        <div v-if="!conversationId" class="tw-h-full tw-flex tw-flex-col tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-min-h-[calc(100vh-200px)] tw-items-center tw-justify-center tw-p-4 sm:tw-p-6 lg:tw-p-8 tw-relative tw-overflow-hidden">
          <!-- Background Pattern -->
          <div class="tw-absolute tw-inset-0 tw-opacity-3">
            <div class="tw-absolute tw-top-10 tw-left-10 tw-w-16 tw-h-16 sm:tw-w-20 sm:tw-h-20 tw-bg-primary-100 tw-rounded-full"></div>
            <div class="tw-absolute tw-bottom-10 tw-right-10 tw-w-12 tw-h-12 sm:tw-w-16 sm:tw-h-16 tw-bg-primary-200 tw-rounded-full"></div>
            <div class="tw-absolute tw-top-1/2 tw-left-1/4 tw-w-8 tw-h-8 sm:tw-w-12 sm:tw-h-12 tw-bg-primary-100 tw-rounded-full"></div>
          </div>
          
          <div class="tw-text-center tw-max-w-sm sm:tw-max-w-md lg:tw-max-w-lg tw-mx-auto tw-relative tw-z-10">
            <!-- Enhanced Icon Container -->
            <div class="tw-w-16 tw-h-16 sm:tw-w-20 sm:tw-h-20 lg:tw-w-24 lg:tw-h-24 tw-bg-gradient-to-br tw-from-primary-500 tw-to-primary-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 sm:tw-mb-6 tw-shadow-lg tw-shadow-primary-200">
              <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-xl sm:tw-text-2xl lg:tw-text-3xl tw-text-white" />
            </div>
            
            <!-- Enhanced Title -->
            <h3 class="tw-text-lg sm:tw-text-xl lg:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2 sm:tw-mb-3 tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-700 tw-bg-clip-text tw-text-transparent">
              {{ $t('messages.empty-state.title', 'Start a Conversation') }}
            </h3>
            
            <!-- Enhanced Description -->
            <p class="tw-text-sm sm:tw-text-base lg:tw-text-lg tw-text-gray-600 tw-mb-6 sm:tw-mb-8 tw-leading-relaxed tw-px-2">
              {{ $t('messages.empty-state.description', 'Choose a conversation from the list to begin messaging with other users.') }}
              <span class="tw-font-medium tw-text-gray-700 tw-block tw-mt-1">
                {{ $t('messages.empty-state.sub-description', 'Your messages will appear here once you select a conversation.') }}
              </span>
            </p>
            
            <!-- Feature Highlights -->
            <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3 sm:tw-gap-4 tw-mb-6 sm:tw-mb-8 tw-max-w-2xl tw-mx-auto">
              <div class="tw-flex tw-items-center tw-gap-2 sm:tw-gap-3 tw-p-2 sm:tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                <div class="tw-w-6 tw-h-6 sm:tw-w-8 sm:tw-h-8 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-xs sm:tw-text-sm" />
                </div>
                <span class="tw-text-xs sm:tw-text-sm tw-font-medium tw-text-gray-700">{{ $t('messages.empty-state.feature-1', 'Connect with users') }}</span>
              </div>
              
              <div class="tw-flex tw-items-center tw-gap-2 sm:tw-gap-3 tw-p-2 sm:tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                <div class="tw-w-6 tw-h-6 sm:tw-w-8 sm:tw-h-8 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-primary-600 tw-text-xs sm:tw-text-sm" />
                </div>
                <span class="tw-text-xs sm:tw-text-sm tw-font-medium tw-text-gray-700">{{ $t('messages.empty-state.feature-2', 'Real-time messaging') }}</span>
              </div>
              
              <div class="tw-flex tw-items-center tw-gap-2 sm:tw-gap-3 tw-p-2 sm:tw-p-3 tw-bg-gray-50 tw-rounded-lg sm:tw-col-span-2 lg:tw-col-span-1">
                <div class="tw-w-6 tw-h-6 sm:tw-w-8 sm:tw-h-8 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                  <icon icon="solar:shield-check-bold-duotone" class="tw-text-primary-600 tw-text-xs sm:tw-text-sm" />
                </div>
                <span class="tw-text-xs sm:tw-text-sm tw-font-medium tw-text-gray-700">{{ $t('messages.empty-state.feature-3', 'Secure & private') }}</span>
              </div>
            </div>
            
            <!-- Call to Action -->
            <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-3 tw-justify-center tw-items-center">
              <div class="tw-flex tw-items-center tw-gap-2 tw-text-xs sm:tw-text-sm tw-text-gray-500">
                <icon icon="solar:info-circle-bold-duotone" class="tw-text-primary-500" />
                <span>{{ $t('messages.empty-state.call-to-action', 'Select a conversation from the left panel') }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Chat View when conversation is selected -->
        <ChatView :conversationId="conversationId" :key="conversationId" v-else />
      </div>
    </div>
  </div>
</template>

<script setup>
import ConversationList from '@/components/messages/ConversationList.vue'
import ChatView from '@/components/messages/ChatView.vue'
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const conversationId = ref(route.params.id ?? null);



watch(() => route.params.id, async (newId) => {
  if (newId) {
    conversationId.value = route.params.id;
  }
})
</script>

<style scoped>
</style>